import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { BlogPost } from '../../types/blog.ts';
import { Film, Tv, Star } from 'lucide-react';

const Container = styled.div`
  margin: var(--spacing-6) 0;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-4);
  padding: 0 var(--spacing-4);
`;

const StyledSwiper = styled(Swiper)`
  .swiper-slide {
    height: auto;
    padding: var(--spacing-2);
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: var(--color-primary);
    
    &:hover {
      color: var(--color-primary-dark);
    }

    &::after {
      font-size: 24px;
    }
  }

  .swiper-pagination-bullet {
    background: var(--color-primary);
    opacity: 0.5;

    &.swiper-pagination-bullet-active {
      opacity: 1;
    }
  }
`;

const Card = styled.div`
  position: relative;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DefaultImage = styled.div`
  width: 100%;
  height: 100%;
  background: var(--color-background-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--spacing-4);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
  color: white;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-2);
`;

const CardMeta = styled.div`
  display: flex;
  gap: var(--spacing-3);
  align-items: center;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
`;

interface BlogCarouselProps {
  posts: BlogPost[];
}

export const BlogCarousel: React.FC<BlogCarouselProps> = ({ posts }) => {
  const navigate = useNavigate();

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <Container>
      <Title>Articles récents</Title>
      <StyledSwiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={20}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        autoplay={{
          delay: 3000,
          disableOnInteraction: true,
        }}
        breakpoints={{
          640: {
            slidesPerView: 2,
          },
          1024: {
            slidesPerView: 3,
          },
        }}
      >
        {posts.map((post) => (
          <SwiperSlide key={post.id}>
            <Card onClick={() => navigate(`/blog/${post.id}`)}>
              {post.image ? (
                <CardImage src={post.image} alt={post.title} />
              ) : (
                <DefaultImage>
                  <Film size={48} />
                </DefaultImage>
              )}
              <CardOverlay>
                <CardTitle>{post.title}</CardTitle>
                <CardMeta>
                  <MetaItem>
                    {post.type === 'film' ? <Film size={16} /> : <Tv size={16} />}
                    {post.type === 'film' ? 'Film' : 'Série'}
                  </MetaItem>
                  <MetaItem>
                    <Star size={16} />
                    {post.rating}/10
                  </MetaItem>
                </CardMeta>
              </CardOverlay>
            </Card>
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </Container>
  );
};
